import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Check, CircleSlash, Minus } from "lucide-react";
import PageShell, { Section } from "@/components/site/PageShell";
import { Note, Prose, Table } from "@/components/site/ui";
import { Inline } from "@/components/site/Renderer";
import { AI_TRANSPARENCY, COMPLIANCE, CONTROLS, DISCLOSURE, DOCUMENTS, POSTURE, SUBPROCESSORS } from "@/content/trust";

const STATE_META = {
  ok: { icon: Check, label: "En place", cls: "is-ok" },
  partial: { icon: AlertTriangle, label: "Partiel", cls: "is-partial" },
  todo: { icon: Minus, label: "Pas encore", cls: "is-todo" },
};

function ControlList({ items }) {
  return (
    <div className="ctrl-list">
      {items.map(it => {
        const meta = STATE_META[it.state];
        const Icon = meta.icon;
        return (
          <div key={it.title} className={`ctrl ${meta.cls}`}>
            <span className="ctrl-state" title={meta.label}><Icon size={12} /></span>
            <div>
              <strong>{it.title}<em>{meta.label}</em></strong>
              <p>{it.text}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------------- Trust Center ---------------- */
export function TrustCenter() {
  const stats = CONTROLS.flatMap(g => g.items);
  const ok = stats.filter(i => i.state === "ok").length;
  return (
    <PageShell eyebrow="Trust Center" title="Ce qui protège vos données."
      lead={POSTURE.summary}
      actions={<>
        <Link to="/securite" className="button button-dark">Détail des mesures<ArrowRight size={13} /></Link>
        <Link to="/statut" className="button button-outline">État des services</Link>
      </>}>
      <Section>
        <div className="trust-stats">
          <div><strong>{ok}</strong><span>mesures en place</span></div>
          <div><strong>{stats.length - ok}</strong><span>points encore ouverts</span></div>
          <div><strong>0</strong><span>certification obtenue</span></div>
          <div><strong>{POSTURE.updated}</strong><span>dernière mise à jour</span></div>
        </div>
        <Note kind="info">
          Nous affichons le nombre de points ouverts au même endroit que nos acquis.
          Une page de confiance qui ne montre que des réussites n'apprend rien à personne.
        </Note>
      </Section>

      {CONTROLS.map(g => (
        <Section key={g.group} id={g.id} title={g.group}><ControlList items={g.items} /></Section>
      ))}

      <Section title="Conformité" lead="Statut réel de chaque référentiel. Aucune certification n'est revendiquée tant qu'elle n'est pas obtenue.">
        <div className="compliance">
          {COMPLIANCE.map(c => (
            <div key={c.name} className={`compliance-row is-${c.state}`}>
              <strong>{c.name}</strong>
              <span className="compliance-tag">
                {c.state === "applicable" ? "Applicable" : c.state === "na" ? "Sans objet" : "Non certifié"}
              </span>
              <p>{c.text}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Documents" lead="Ce que nous pouvons vous transmettre aujourd'hui.">
        <div className="card-grid">
          {DOCUMENTS.map(d => (
            <div key={d.title} className={`info-card${d.available ? "" : " is-muted"}`}>
              <h3>{d.title}{!d.available && <CircleSlash size={13} style={{ marginLeft: 7, color: "#b0b8a8", verticalAlign: "-2px" }} />}</h3>
              <p>{d.text}</p>
              {d.available && d.to && <Link to={d.to}>Consulter<ArrowRight size={12} /></Link>}
            </div>
          ))}
        </div>
      </Section>

      <Section title="Contact sécurité">
        <Prose>
          <p>Pour toute question de sécurité : <a href="mailto:securite@performcustom.com">securite@performcustom.com</a>.</p>
          <p>Pour signaler une vulnérabilité, suivez la procédure décrite sur la page <Link to="/securite/divulgation">Divulgation de vulnérabilité</Link>.</p>
        </Prose>
      </Section>
    </PageShell>
  );
}

/* ---------------- Sécurité ---------------- */
export function Security() {
  return (
    <PageShell eyebrow="Sécurité" title="Comment Perform protège votre compte."
      lead="Le détail technique, mesure par mesure. Ce qui manque est indiqué aussi clairement que ce qui est en place."
      actions={<Link to="/trust-center" className="button button-dark">Voir le Trust Center<ArrowRight size={13} /></Link>}>
      {CONTROLS.map(g => (
        <Section key={g.group} id={g.id} title={g.group}><ControlList items={g.items} /></Section>
      ))}
      <Section title="Signaler une faille">
        <Prose>
          <p>
            Vous avez trouvé quelque chose ? Écrivez à <a href="mailto:securite@performcustom.com">securite@performcustom.com</a>.
            La procédure complète, le périmètre et nos délais de réponse sont sur la
            page <Link to="/securite/divulgation">Divulgation de vulnérabilité</Link>.
          </p>
        </Prose>
      </Section>
    </PageShell>
  );
}

/* ---------------- Divulgation de vulnérabilité ---------------- */
export function Disclosure() {
  return (
    <PageShell eyebrow="Sécurité" title="Divulgation de vulnérabilité"
      lead="Si vous trouvez une faille, dites-le-nous. Nous ne poursuivrons jamais quelqu'un qui respecte les règles ci-dessous.">
      <Section title="Comment signaler">
        <Prose>
          <p>Un seul canal : <a href={`mailto:${DISCLOSURE.email}`}>{DISCLOSURE.email}</a>.</p>
          <p><strong>Nos délais :</strong> {DISCLOSURE.sla}</p>
        </Prose>
        <Note kind="info">
          Nous n'avons pas de programme de récompense (bug bounty) : nous ne pouvons pas encore le financer,
          et nous préférons le dire plutôt que de laisser espérer une prime.
        </Note>
      </Section>
      <Section title="Périmètre">
        <div className="card-grid cols-2">
          <div className="info-card">
            <h3>Dans le périmètre</h3>
            <ul className="rule-list is-do">{DISCLOSURE.inScope.map(s => <li key={s}><Check size={13} />{s}</li>)}</ul>
          </div>
          <div className="info-card">
            <h3>Hors périmètre</h3>
            <ul className="rule-list is-dont">{DISCLOSURE.outOfScope.map(s => <li key={s}><Minus size={13} />{s}</li>)}</ul>
          </div>
        </div>
      </Section>
      <Section title="Règles du jeu">
        <Prose><ul>{DISCLOSURE.rules.map(r => <li key={r}>{r}</li>)}</ul></Prose>
      </Section>
      <Section title="Ce qu'il nous faut">
        <Prose><ul>{DISCLOSURE.include.map(r => <li key={r}>{r}</li>)}</ul></Prose>
      </Section>
    </PageShell>
  );
}

/* ---------------- Conformité ---------------- */
export function Compliance() {
  return (
    <PageShell eyebrow="Conformité" title="Où nous en sommes, référentiel par référentiel."
      lead="Un état des lieux honnête plutôt qu'une collection de logos.">
      <Section>
        <div className="compliance">
          {COMPLIANCE.map(c => (
            <div key={c.name} className={`compliance-row is-${c.state}`}>
              <strong>{c.name}</strong>
              <span className="compliance-tag">
                {c.state === "applicable" ? "Applicable" : c.state === "na" ? "Sans objet" : "Non certifié"}
              </span>
              <p>{c.text}</p>
            </div>
          ))}
        </div>
      </Section>
      <Section title="Pourquoi aucune certification ?">
        <Prose>
          <p>
            Une certification SOC 2 ou ISO 27001 coûte plusieurs dizaines de milliers d'euros et suppose
            des mois d'audit. Perform vient d'ouvrir : l'engager aujourd'hui reviendrait à acheter un tampon
            avant d'avoir les processus qu'il est censé attester.
          </p>
          <p>
            Notre priorité va d'abord aux manques concrets listés dans le <Link to="/trust-center">Trust Center</Link>,
            à commencer par les sauvegardes automatiques. Une certification viendra quand elle attestera quelque chose de réel.
          </p>
        </Prose>
      </Section>
      <Section title="Pour les structures">
        <Prose>
          <p>
            Si vous devez remplir un questionnaire de sécurité fournisseur, écrivez à <a href="mailto:securite@performcustom.com">securite@performcustom.com</a> :
            nous répondons point par point, y compris quand la réponse est « non ».
          </p>
        </Prose>
      </Section>
    </PageShell>
  );
}

/* ---------------- Centre de confidentialité ---------------- */
export function PrivacyCenter() {
  return (
    <PageShell eyebrow="Confidentialité" title="Vos données, en clair."
      lead="Ce que nous collectons, pourquoi, combien de temps, et comment reprendre la main."
      actions={<>
        <a className="button button-dark" href="mailto:confidentialite@performcustom.com?subject=Demande%20d%27acc%C3%A8s%20%C3%A0%20mes%20donn%C3%A9es" data-testid="privacy-request">Demander mes données</a>
        <a className="button button-outline" href="mailto:confidentialite@performcustom.com?subject=Demande%20de%20suppression%20de%20mes%20donn%C3%A9es" data-testid="privacy-delete">Supprimer mes données</a>
      </>}>
      <Section title="Ce que nous collectons">
        <Table head={["Donnée", "Pourquoi", "Conservation"]} rows={[
          ["Adresse e-mail", "Connexion et notifications de service", "Durée du compte"],
          ["Pseudo, prénom, nom", "Affichage du profil", "Durée du compte"],
          ["Photo de profil", "Affichage du profil", "Jusqu'au retrait"],
          ["Jeu, palier, objectif", "Recommandation de coachs", "Durée du compte"],
          ["Sessions", "Maintien de la connexion", "30 jours"],
          ["Liens de connexion", "Authentification", "15 minutes"],
        ]} />
      </Section>
      <Section title="Ce que nous ne collectons pas">
        <Prose>
          <ul>
            <li>Aucune donnée bancaire : les paiements ne sont pas activés.</li>
            <li>Aucun traceur publicitaire, aucun pixel, aucune régie.</li>
            <li>Aucun outil de mesure d'audience tiers à ce jour.</li>
            <li>Aucune donnée de santé, aucune donnée sensible au sens du RGPD.</li>
          </ul>
        </Prose>
      </Section>
      <Section title="Vos droits">
        <Prose>
          <p>Accès, rectification, effacement, limitation, opposition et portabilité. Écrivez à <a href="mailto:confidentialite@performcustom.com">confidentialite@performcustom.com</a> depuis l'adresse de votre compte : nous répondons sous un mois.</p>
          <p>Si notre réponse ne vous satisfait pas, vous pouvez saisir la CNIL.</p>
        </Prose>
      </Section>
      <Section title="Où vont vos données">
        <Prose><p>Cinq prestataires interviennent, tous listés avec leur finalité et leur localisation sur la page <Link to="/legal/sous-traitants">Sous-traitants</Link>.</p></Prose>
        <Note>Nos prestataires sont établis aux États-Unis : vos données sont transférées hors de l'Union européenne. L'encadrement contractuel de ces transferts est en cours de formalisation.</Note>
      </Section>
      <Section title="Documents">
        <Prose>
          <ul>
            <li><Link to="/legal/confidentialite">Politique de confidentialité</Link> — le document complet.</li>
            <li><Link to="/legal/cookies">Politique de cookies</Link> — les trois cookies déposés.</li>
            <li><Link to="/legal/dpa">Accord de traitement des données</Link> — pour les clubs et structures.</li>
          </ul>
        </Prose>
      </Section>
    </PageShell>
  );
}

/* ---------------- Sous-traitants ---------------- */
export function Subprocessors() {
  return (
    <PageShell eyebrow="Sous-traitants" title="Qui traite vos données."
      lead={`Liste exhaustive des prestataires ayant accès à des données. Dernière mise à jour : ${SUBPROCESSORS.updated}.`}>
      <Section>
        <Table
          head={["Prestataire", "Finalité", "Données traitées", "Localisation"]}
          rows={SUBPROCESSORS.rows.map(r => [r.provider, r.purpose, r.data, `${r.location} (${r.country})`])}
        />
      </Section>
      <Section title="Transferts hors Union européenne">
        <Note>
          Tous nos prestataires sont établis aux États-Unis. Ces transferts doivent être encadrés par des
          clauses contractuelles types : la formalisation avec chaque prestataire est en cours. Nous
          l'indiquons plutôt que de laisser croire que c'est déjà fait.
        </Note>
      </Section>
      <Section title="Changements">
        <Prose>
          <p>
            Tout nouveau sous-traitant sera ajouté à cette page avant sa mise en service. Les structures liées
            par un <Link to="/legal/dpa">accord de traitement</Link> en sont informées et peuvent s'y opposer.
          </p>
        </Prose>
      </Section>
    </PageShell>
  );
}

/* ---------------- Transparence IA ---------------- */
export function AITransparency() {
  return (
    <PageShell eyebrow="Transparence IA" title="Aucune donnée n'est envoyée à un modèle."
      lead={AI_TRANSPARENCY.state}>
      <Section>
        <Note kind="info">
          Nous publions cette page alors même que nous n'utilisons pas encore d'IA. C'est volontaire :
          les règles sont posées avant l'activation, pas après.
        </Note>
      </Section>
      <Section title="Nos engagements">
        <div className="ctrl-list">
          {AI_TRANSPARENCY.points.map(p => (
            <div key={p.title} className="ctrl is-ok">
              <span className="ctrl-state"><Check size={12} /></span>
              <div><strong>{p.title}</strong><p><Inline text={p.text} /></p></div>
            </div>
          ))}
        </div>
      </Section>
      <Section title="Documents liés">
        <Prose>
          <ul>
            <li><Link to="/legal/conditions-ia">Conditions relatives à l'IA</Link></li>
            <li><Link to="/legal/sous-traitants">Liste des sous-traitants</Link> — les fournisseurs de modèles y figureront.</li>
          </ul>
        </Prose>
      </Section>
    </PageShell>
  );
}
